<script lang="ts">
	import Letterhead from '$lib/components/Letterhead.svelte';
	import HipaaShield from '$lib/components/HipaaShield.svelte';
	import { canonicalUrl, jsonLdGraph, ogImageUrl, pageTitle } from '$lib/seo';

	let { data } = $props();
	const meta = $derived(data.seo!);

	const lastEdited = 'May 2026';
</script>

<svelte:head>
	<title>{pageTitle(meta)}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={canonicalUrl(meta)} />
	<meta property="og:type" content="website" />
	<meta property="og:url" content={canonicalUrl(meta)} />
	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:image" content={ogImageUrl(meta)} />
	<meta name="twitter:card" content="summary_large_image" />
	{@html `<script type="application/ld+json">${jsonLdGraph(meta)}</script>`}
</svelte:head>

<article class="hipaa">
	<div class="container-wide">
		<Letterhead>
			<header class="hipaa-head">
				<div class="hipaa-head-row">
					<div>
						<h1 class="hipaa-title">On HIPAA.</h1>
						<p class="hipaa-lede">
							Two questions matter on call one — are we HIPAA compliant, and how do we know. We are.
							Below is what we do.
						</p>
					</div>
					<HipaaShield size={88} link={false} />
				</div>
			</header>

			<section class="block">
				<h2>The short version.</h2>
				<p>
					We are <strong>HIPAA compliant</strong> and we take the work seriously. Patient data is encrypted
					at rest and in transit. Every login uses two-factor. Every action — ours and your staff's —
					is recorded. We store only what we need. We sign a BAA written in plain English within twenty-four
					hours. If you ever leave, you take everything with you in ninety days, no negotiation.
				</p>
				<p>
					We also held ourselves to the new <strong>2026 HIPAA Security Rule</strong>
					before it was finalized. The rule formalizes what we already built.
				</p>
			</section>

			<section class="block">
				<h2>What HIPAA actually asks of a vendor like us.</h2>
				<p>
					Three categories — administrative (the program), physical (the building), and technical
					(the software). We carry obligations in all three because patient data flows through
					Ekavi. Below is each thing the law asks, in plain language, and what we do about it.
				</p>
			</section>

			<section class="block">
				<h2>How we cover each requirement.</h2>
				<ol class="reqs">
					<li class="req">
						<p class="req-title">Encryption</p>
						<p class="req-body">
							Patient data is encrypted on disk and over the wire. AES-256 at rest, TLS 1.3 in
							transit. No fallback to weaker settings.
						</p>
					</li>
					<li class="req">
						<p class="req-title">Access control</p>
						<p class="req-body">
							Two-factor authentication on every login. Each person on your team sees only what
							their job needs. Nothing more.
						</p>
					</li>
					<li class="req">
						<p class="req-title">Audit log</p>
						<p class="req-body">
							Every action recorded — what was done, when, by whom, with what result. Seven years of
							retention. Yours to export, in CSV or signed PDF, without a support ticket.
						</p>
					</li>
					<li class="req">
						<p class="req-title">Data we keep to the minimum</p>
						<p class="req-body">
							We store only the patient data Ekavi needs to do its job. No silent analytics copy of
							your patients. No data lake. The default retention is ninety days; you can change it.
						</p>
					</li>
					<li class="req">
						<p class="req-title">Managed AI, US providers, no model training</p>
						<p class="req-body">
							When a model reads chart text, the request goes through enterprise-tier US-based AI
							providers under signed BAAs and a zero-retention agreement. Patient names are replaced
							with opaque tokens before processing where possible. Your patient data <strong
								>never trains a model</strong
							>. Ever.
						</p>
					</li>
					<li class="req">
						<p class="req-title">Cloud hosting</p>
						<p class="req-body">
							Ekavi runs on Google Cloud's HIPAA-eligible infrastructure under a signed BAA. Your
							practice's data sits behind its own boundary; nothing crosses between practices.
						</p>
					</li>
					<li class="req">
						<p class="req-title">Annual verification</p>
						<p class="req-body">
							Once a year, every January, a written compliance packet lands in your inbox — already
							signed and dated, ready for your auditor. No support ticket. No call.
						</p>
					</li>
					<li class="req">
						<p class="req-title">Workforce training</p>
						<p class="req-body">
							Every person on the Ekavi team completes HIPAA training annually. Records are kept and
							inspectable.
						</p>
					</li>
					<li class="req">
						<p class="req-title">Incident response</p>
						<p class="req-body">
							If something fails or is breached, you hear from us within four hours. The note says
							what we know, what we are doing, and when the next signal will come. No theatre.
						</p>
					</li>
					<li class="req">
						<p class="req-title">Sub-processors, named</p>
						<p class="req-body">
							Every third party that touches your data is published. Changes get thirty days'
							written notice. No surprises.
						</p>
					</li>
					<li class="req">
						<p class="req-title">A clean exit, in writing</p>
						<p class="req-body">
							The MSA includes a ninety-day clean export clause. If you decide to leave, your data
							exports in standard formats, your account closes on a date you choose, and access
							continues for ninety days after. No surrender to negotiate.
						</p>
					</li>
				</ol>
			</section>

			<section class="block">
				<h2>What the 2026 Security Rule changed.</h2>
				<p>
					For the first time since 2013, the federal government rewrote the HIPAA Security Rule. The
					rule formalizes what most serious vendors were already doing — and closes the loopholes
					the others were hiding behind. The headline: every safeguard is now required, not
					"addressable."
				</p>
				<ul class="changes">
					<li>Multi-factor authentication on every interactive login. <em>Required.</em></li>
					<li>Encryption at rest and in transit. <em>Required.</em></li>
					<li>Annual compliance audit and written report. <em>Required.</em></li>
					<li>An asset inventory of every system that touches patient data. <em>Required.</em></li>
					<li>Vulnerability scanning on a fixed schedule. <em>Required.</em></li>
					<li>Network segmentation between systems. <em>Required.</em></li>
					<li>Stricter incident-response and breach-notification timelines. <em>Required.</em></li>
				</ul>
				<p>
					We were already running to that standard. We re-checked everything against the new rule.
					We are compliant.
				</p>
			</section>

			<section class="block">
				<h2>A BAA written for AI.</h2>
				<p>
					The Business Associate Agreement is the contract that authorizes Ekavi to handle PHI on
					your behalf. Most vendor BAAs were written for traditional software; ours is written for
					AI:
				</p>
				<ul>
					<li>Patient data does not train any model. Ever. Not for our model. Not for any.</li>
					<li>Default retention is ninety days. Configurable.</li>
					<li>Sub-processors named in the BAA, changeable with thirty days' written notice.</li>
					<li>Plain language. Signed within twenty-four hours.</li>
				</ul>
			</section>

			<section class="block">
				<h2>What we are not claiming.</h2>
				<p>
					There is no government-issued "HIPAA certification." HHS does not certify vendors. When we
					say we are HIPAA compliant, we mean we have the program in place, we test it regularly,
					and we are willing to put it in writing — in the BAA, in the verification packet, and in
					the audit log every action leaves behind.
				</p>
				<p>
					If your auditor or counsel needs more — the full security architecture, the
					verification packet, audit-log spec, sub-processor list, incident-response posture —
					we send it on request. Every practice on Ekavi receives it automatically — every
					January, on a fixed cadence, signed and dated. The <a href="/trust/">trust page</a> is the public summary; for
					specific questions, the kind that need a signature, write to
					<a href="mailto:trust@ekavi.care">trust@ekavi.care</a>.
				</p>
			</section>

			<footer class="hipaa-foot">
				<hr class="hipaa-rule" />
				<p class="hipaa-meta">Last edited {lastEdited}.</p>
			</footer>
		</Letterhead>
	</div>
</article>

<style>
	.hipaa {
		padding: var(--ekavi-space-12) 0 var(--ekavi-space-24);
	}
	.hipaa-head {
		margin-bottom: var(--ekavi-space-12);
	}
	.hipaa-head-row {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		gap: var(--ekavi-space-9);
		flex-wrap: wrap;
	}
	.hipaa-title {
		font-family: var(--ekavi-font-ui);
		font-weight: var(--ekavi-weight-kraftig);
		font-size: var(--ekavi-text-40);
		letter-spacing: var(--ekavi-track-headers);
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-6);
	}
	@media (max-width: 640px) {
		.hipaa-title {
			font-size: var(--ekavi-text-28);
		}
	}
	.hipaa-lede {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-20);
		color: var(--ekavi-ink-2);
		line-height: var(--ekavi-leading-body);
		max-width: 38ch;
		margin: 0;
	}

	.block {
		padding: var(--ekavi-space-9) 0;
		border-top: var(--ekavi-stroke-hairline);
	}
	.block:first-of-type {
		border-top: none;
	}
	.block h2 {
		font-family: var(--ekavi-font-ui);
		font-weight: var(--ekavi-weight-kraftig);
		font-size: var(--ekavi-text-20);
		letter-spacing: var(--ekavi-track-headers);
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-4);
	}
	.block p {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-16);
		line-height: var(--ekavi-leading-body);
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-4);
		max-width: 62ch;
	}
	.block p:last-child {
		margin-bottom: 0;
	}
	.block ul {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-16);
		color: var(--ekavi-ink-1);
		padding-left: 0;
		margin: 0 0 var(--ekavi-space-4);
		list-style: none;
		max-width: 62ch;
	}
	.block ul li {
		padding: var(--ekavi-space-2) 0;
		border-bottom: var(--ekavi-stroke-hairline);
		line-height: var(--ekavi-leading-body);
	}
	.block ul li:last-child {
		border-bottom: none;
	}
	.block ul li em {
		font-style: italic;
		color: var(--ekavi-ink-3);
	}

	/* The eleven requirements list — numbered, hairline-separated, each
	   row is title + plain-English answer. */
	.reqs {
		list-style: none;
		counter-reset: req;
		padding: 0;
		margin: 0;
	}
	.req {
		counter-increment: req;
		padding: var(--ekavi-space-4) 0;
		border-top: var(--ekavi-stroke-hairline);
		display: grid;
		grid-template-columns: 36px minmax(0, 1fr);
		column-gap: var(--ekavi-space-4);
		row-gap: 2px;
	}
	.req:first-child {
		border-top: none;
	}
	.req::before {
		content: counter(req, decimal-leading-zero);
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		letter-spacing: var(--ekavi-track-caps);
		color: var(--ekavi-ochre);
		font-variant-numeric: tabular-nums;
		font-weight: var(--ekavi-weight-halbfett);
		line-height: 1.4;
	}
	.req-title {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-16);
		font-weight: var(--ekavi-weight-kraftig);
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-1);
		grid-column: 2;
	}
	.req-body {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-16);
		line-height: var(--ekavi-leading-body);
		color: var(--ekavi-ink-2);
		margin: 0;
		grid-column: 2;
		max-width: 60ch;
	}

	.hipaa-foot {
		margin-top: var(--ekavi-space-12);
	}
	.hipaa-rule {
		border: none;
		border-top: var(--ekavi-stroke-hairline);
		margin: 0 0 var(--ekavi-space-9);
	}
	.hipaa-meta {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-3);
		margin: 0;
	}
</style>
